var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, computed, makeObservable } from 'mobx';
import UserActionsAPI from '../services/UserActionsAPI';
import compareStore from './CompareStore';
import { CARS_LIMIT } from '../framework/constants/cars';
import { FAVORITES_SORT_DEFAULT } from '../framework/constants/data';
import { HTTP_TO_MANY_REQUESTS } from '../framework/constants/app';
import { ShowNotificationError } from '../framework/ErrorLogger';
import { transformDataForVehicles } from '../framework/DataTransformers';
class FavoriteStore {
    constructor() {
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "favoritesIDs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "favoriteCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable([])
        });
        Object.defineProperty(this, "activeSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: FAVORITES_SORT_DEFAULT
        });
        Object.defineProperty(this, "getFavoritesIDs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return UserActionsAPI.getFavoritesIDs()
                    .then(({ data }) => {
                    this.favoritesIDs = data;
                    return data;
                })
                    .catch((error) => {
                    this.favoritesIDs = [];
                    ShowNotificationError('errors.loadFavorites');
                });
            }
        });
        Object.defineProperty(this, "getFavouriteCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page = 1) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                if (this.favoriteCars.length !== 0) {
                    return;
                }
                this.isFetching = true;
                return UserActionsAPI.getFavoriteCars(offset, this.activeSorting)
                    .then((cars) => {
                    this.favoriteCars.clear();
                    if (!cars)
                        throw new Error('No vehicles available');
                    for (let i = 0; i < cars.length; i++) {
                        this.favoriteCars.push(transformDataForVehicles(cars[i]));
                    }
                    this.isFetching = false;
                })
                    .catch((error) => {
                    ShowNotificationError('errors.loadFavorites');
                    this.favoriteCars = observable([]);
                    this.isFetching = false;
                    return [];
                });
            }
        });
        Object.defineProperty(this, "toggleFavoriteCarStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID) => {
                return this.getFavoritesIDs()
                    .then((ids) => {
                    const isPresentAlready = ids.find((id) => id === vehicleID);
                    return isPresentAlready ?
                        this.removeCarFromFavorites(vehicleID) :
                        UserActionsAPI.addFavorite(vehicleID);
                })
                    .then((cars) => {
                    this.getFavoritesIDs().then((res) => res);
                })
                    .catch((error) => {
                    if (error && typeof error === 'object' && error.status === HTTP_TO_MANY_REQUESTS) {
                        const { location: { pathname, search } } = window;
                        const redirectTo = `${UserActionsAPI.config.baseURL}/user/favorite/car/${vehicleID}?url=${pathname}${encodeURIComponent(search)}`;
                        window.location.href = redirectTo;
                    }
                });
            }
        });
        Object.defineProperty(this, "removeCarFromFavorites", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID) => {
                return UserActionsAPI.removeFavorite(vehicleID)
                    .then((data) => {
                    this.favoritesIDs = this.favoritesIDs.filter(id => id !== vehicleID);
                    this.favoriteCars = this.favoriteCars.filter(item => item.id !== vehicleID);
                    compareStore.removeVehicleIDFromCompare(vehicleID);
                    return data;
                })
                    .catch(() => {
                    ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "moveFavoritesToUserAccount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => UserActionsAPI.moveFavoritesToUserAccount()
        });
        Object.defineProperty(this, "changeSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (sorting) => {
                this.activeSorting = sorting;
            }
        });
        Object.defineProperty(this, "clearState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.favoriteCars = observable([]);
                this.activeSorting = FAVORITES_SORT_DEFAULT;
            }
        });
        makeObservable(this);
    }
    get showList() {
        return this.favoriteCars && this.favoriteCars.length > 0;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], FavoriteStore.prototype, "favoritesIDs", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "favoriteCars", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], FavoriteStore.prototype, "activeSorting", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], FavoriteStore.prototype, "showList", null);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "getFavoritesIDs", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "getFavouriteCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "toggleFavoriteCarStatus", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "removeCarFromFavorites", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "moveFavoritesToUserAccount", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "changeSorting", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FavoriteStore.prototype, "clearState", void 0);
export default new FavoriteStore();
